import React, { useCallback, useEffect, useState } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, XCircle, Banknote } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";

const STATUS_COLORS = {
  pending:    "bg-amber-100 text-amber-800",
  processing: "bg-sky-100 text-sky-800",
  refunded:   "bg-emerald-100 text-emerald-800",
  rejected:   "bg-red-100 text-red-800",
  approved:   "bg-emerald-100 text-emerald-800",
};

export default function AdminRefunds() {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);    // refund being acted on
  const [action, setAction] = useState(null);     // "approve" | "reject"
  const [adminNote, setAdminNote] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const q = status ? `?status=${status}` : "";
      const [r1, r2] = await Promise.all([
        api.get(`/admin/refunds${q}`),
        api.get(`/admin/refunds/stats`),
      ]);
      setItems(r1.data || []);
      setStats(r2.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load refunds");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openAction = (refund, act) => {
    setTarget(refund);
    setAction(act);
    setAdminNote("");
    setAmount(act === "approve" ? String(refund.amount || refund.order?.total || 0) : "");
  };

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const url = `/admin/refunds/${target.id}/${action}`;
      const body = { admin_note: adminNote.trim() || undefined };
      if (action === "approve" && amount) body.amount = Number(amount);
      await api.put(url, body);
      toast.success(action === "approve" ? "Refund processed" : "Refund rejected");
      setTarget(null); setAction(null); setAdminNote(""); setAmount("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-refunds-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline">Refunds</div>
          <h1 className="font-display text-4xl">Refund Queue</h1>
        </div>
        <button className="kb-btn-outline flex items-center gap-2" onClick={load} data-testid="refresh-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="refund-stats">
          <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
            <div className="overline">Pending</div>
            <div className="font-display text-2xl mt-1" data-testid="stat-pending">{stats.by_status?.pending?.count || 0}</div>
            <div className="text-xs text-[hsl(var(--kb-muted))]">{money(stats.by_status?.pending?.total_amount || 0)}</div>
          </div>
          <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
            <div className="overline">Refunded</div>
            <div className="font-display text-2xl mt-1" data-testid="stat-refunded">{stats.by_status?.refunded?.count || 0}</div>
            <div className="text-xs text-[hsl(var(--kb-muted))]">{money(stats.by_status?.refunded?.total_amount || 0)}</div>
          </div>
          <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
            <div className="overline">Rejected</div>
            <div className="font-display text-2xl mt-1">{stats.by_status?.rejected?.count || 0}</div>
          </div>
          <div className="bg-white border border-[hsl(var(--kb-border))] p-4">
            <div className="overline">Orders w/ needs_refund</div>
            <div className="font-display text-2xl mt-1" data-testid="stat-orphaned">{stats.orphaned || 0}</div>
            <div className="text-xs text-[hsl(var(--kb-muted))]">No refund request yet</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["", "pending", "refunded", "rejected"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`px-3 py-1 text-xs uppercase tracking-wider border ${status === s ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))]"}`}
            data-testid={`filter-status-${s || "all"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {error && <div className="p-3 border border-red-300 bg-red-50 text-red-800 text-sm mb-4">{error}</div>}
      {loading ? (
        <div className="text-[hsl(var(--kb-muted))]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-[hsl(var(--kb-border))]">
          <Banknote size={28} className="mx-auto text-[hsl(var(--kb-muted))] mb-2" />
          <p className="text-[hsl(var(--kb-muted))]">No refund requests{status && ` with status "${status}"`}.</p>
        </div>
      ) : (
        <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
          <table className="w-full text-sm" data-testid="refunds-table">
            <thead className="bg-[hsl(var(--kb-surface-alt))]">
              <tr>
                <th className="p-3 text-left">Order</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Reason</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`refund-${r.id}`}>
                  <td className="p-3">
                    <div className="font-mono text-xs">{(r.order?.order_number || r.order_id).slice(0, 14)}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))]">
                      {r.order?.payment_method?.toUpperCase()} · {r.order?.status}
                    </div>
                  </td>
                  <td className="p-3">
                    <div>{r.user?.name || "—"}</div>
                    <div className="text-xs text-[hsl(var(--kb-muted))]">{r.user?.phone}</div>
                  </td>
                  <td className="p-3 max-w-xs">
                    <div className="text-xs">{r.reason}</div>
                    {r.note && <div className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">{r.note}</div>}
                    {r.admin_note && <div className="text-[11px] text-amber-700 mt-1">Admin: {r.admin_note}</div>}
                  </td>
                  <td className="p-3 text-right">{money(r.amount)}</td>
                  <td className="p-3 text-center">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    {r.status === "pending" ? (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => openAction(r, "approve")}
                          className="text-xs uppercase tracking-wider text-emerald-700 hover:underline flex items-center gap-1"
                          data-testid={`refund-approve-${r.id}`}
                        >
                          <CheckCircle size={12} /> Process
                        </button>
                        <button
                          onClick={() => openAction(r, "reject")}
                          className="text-xs uppercase tracking-wider text-red-700 hover:underline flex items-center gap-1"
                          data-testid={`refund-reject-${r.id}`}
                        >
                          <XCircle size={12} /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[hsl(var(--kb-muted))]">
                        {r.processed_at && new Date(r.processed_at).toLocaleDateString()}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminDialog
        open={!!target}
        onClose={() => !busy && setTarget(null)}
        title={action === "approve" ? "Process Refund" : "Reject Refund Request"}
        testId="refund-action-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setTarget(null)} disabled={busy}>Cancel</button>
            <button
              className={action === "approve" ? "kb-btn-primary" : "kb-btn-outline text-red-700 border-red-300"}
              onClick={confirm}
              disabled={busy || (action === "approve" && (!amount || Number(amount) <= 0))}
              data-testid="refund-action-confirm"
            >
              {busy ? "Processing…" : action === "approve" ? "Confirm Refund" : "Reject"}
            </button>
          </>
        }
      >
        {target && (
          <div className="space-y-3 text-sm">
            <div>
              <div className="overline">Customer</div>
              <div>{target.user?.name || target.user?.phone || "—"}</div>
            </div>
            <div>
              <div className="overline">Reason</div>
              <div>{target.reason}</div>
            </div>
            {action === "approve" && (
              <div>
                <div className="overline mb-1">Refund Amount (₹)</div>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="kb-input w-full"
                  min={1}
                  max={target.order?.total || target.amount}
                  data-testid="refund-amount"
                />
                <div className="text-xs text-[hsl(var(--kb-muted))] mt-1">
                  Max: {money(target.order?.total || target.amount)}
                </div>
              </div>
            )}
            <div>
              <div className="overline mb-1">Admin Note (optional)</div>
              <textarea
                rows={2}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm"
                data-testid="refund-admin-note"
              />
            </div>
          </div>
        )}
      </AdminDialog>
    </div>
  );
}
